import { InfoCircleOutlined } from '@ant-design/icons';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import Alert from 'antd/lib/alert';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import DatePicker from 'antd/lib/date-picker';
import Dropdown from 'antd/lib/dropdown';
import Form from 'antd/lib/form';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal/Modal';
import Row from 'antd/lib/row';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { theme } from 'antd/lib';
import InputNumber from 'antd/lib/input-number';
import { useEffect, useMemo, useState } from 'react';
import { toZonedTime } from 'date-fns-tz';
import { differenceInMinutes } from 'date-fns';

import { FORM_ACTION, IAffectedSites } from '../../eventInterface';
import { IStandbyPeriod, IStandbyPeriodFormProps } from '../../../../domain/standby-period/interface';
import { useAuth } from '../../../../domain/auth/useAuth';
import { isCompleted, isStarted, toBatchRequest, toViewStandbyPeriod } from './utils';
import { UserType } from '../../../../domain/user/interface';
import { toAllowedStringLength } from '../../../../domain/common/formattersToAllowedValueLength';
import { ResourceCollapsedList } from '../ResourceCollapsedList/ResourceCollapsedList';
import { ProgramSelector } from '../../../../components/selectors/ProgramSelector/ProgramSelector';
import { EventConfirmationModal } from '../EventConfirmationModal';
import { StandbyPeriodActionType, StandbyPeriodConfirmationModalContent } from './StandbyPeriodConfirmantionModalContent';
import { useStandbyPeriodsInBatchListQuery } from '../../../../domain/standby-period/queries';
import { isPastDate } from '../../utils';
import { CalendarEventPreview } from '../CalendarEventPreview';
import {
    createStandbyPeriodBatch,
    cancelStandbyPeriod,
    cancelStandbyPeriodBatch,
    updateStandbyPeriod,
    updateStandbyPeriodBatch,
} from '../../../../domain/standby-period';

const preparedBatchSites = (batchStandbyPeriods: IStandbyPeriod[]) =>
    (batchStandbyPeriods || []).map(standbyPeriod => ({
        company: standbyPeriod?.site?.company?.company_name!,
        company_id: standbyPeriod?.site?.company?.company_id,
        site: standbyPeriod?.site?.site_name!,
        site_timezone: standbyPeriod?.site?.site_timezone!,
        id: `${standbyPeriod?.site?.site_id!}`,
    }));

const CONFIRMATION_TITLE = {
    [FORM_ACTION.CANCEL]: 'cancel',
    [FORM_ACTION.CANCEL_BATCH]: 'cancel',
    [FORM_ACTION.CANCEL_BATCH_IN_COMPANY]: 'cancel',
    [FORM_ACTION.UPDATE]: 'update',
    [FORM_ACTION.UPDATE_BATCH]: 'update',
    [FORM_ACTION.UPDATE_BATCH_IN_COMPANY]: 'update',
    [FORM_ACTION.CREATE]: 'save',
};

export const StandbyPeriodBySiteFormModal = (props: IStandbyPeriodFormProps) => {
    const { token: themeColors } = theme.useToken();

    const { notification } = App.useApp();
    const [form] = Form.useForm();
    const auth = useAuth();

    const { standbyPeriod, resources, onClose, isEditMode } = props;

    const [loading, setLoading] = useState<boolean>(false);
    const viewStandbyPeriod = useMemo(() => toViewStandbyPeriod(standbyPeriod), [standbyPeriod]);

    const [formAction, setFormAction] = useState<FORM_ACTION | null>(null);
    const [formValid, setFormValid] = useState<boolean>(false);

    const [affectedSites, setAffectedSites] = useState<IAffectedSites[]>(resources);
    const [batchSites, setBatchSites] = useState<IAffectedSites[]>([]);

    const [isLoadingBatchSites, setIsLoadingBatchSites] = useState<boolean>(true);

    const customerCompanyId = auth?.user?.user_type === UserType.CUSTOMER ? auth.user.company_id : undefined;

    const { data: standbyPeriodsInBatch } = useStandbyPeriodsInBatchListQuery(
        {
            batchUuid: standbyPeriod.batch_uuid!,
            include: 'site,company',
            companyId: customerCompanyId,
        },
        { enabled: !!standbyPeriod.batch_uuid }
    );

    useEffect(() => {
        const batchUuid = standbyPeriod?.batch_uuid;

        if (!batchUuid) {
            setBatchSites(resources);
            setIsLoadingBatchSites(false);
            return;
        }

        if (standbyPeriodsInBatch?.length) {
            const sites = preparedBatchSites(standbyPeriodsInBatch);

            setBatchSites(sites);
            setIsLoadingBatchSites(false);
        }
    }, [standbyPeriod, standbyPeriodsInBatch]);

    const datetimeStart = Form.useWatch('datetime_start_local', form);
    const durationHours = Form.useWatch('duration_hours', form);
    const programId = Form.useWatch('program_id', form);

    useEffect(() => {
        (async () => {
            if (!datetimeStart || !durationHours || !programId) {
                setFormValid(false);
                return;
            }

            try {
                await form.validateFields(); // triggers all validators
                setFormValid(true);
            } catch {
                setFormValid(false); // if any validator fails
            }
        })();
    }, [datetimeStart, durationHours, programId, form]);

    const warning = useMemo(
        () =>
            standbyPeriod.is_cancelled || isCompleted(standbyPeriod as IStandbyPeriod) ? (
                <Alert
                    showIcon
                    message="Warning"
                    description="Cannot edit a standby period which has been canceled or has already completed."
                    type="warning"
                    style={{ display: 'flex', alignItems: 'center' }}
                />
            ) : null,
        [standbyPeriod]
    );

    function setDurationToNow() {
        if (isCompleted(standbyPeriod as IStandbyPeriod)) {
            form.setFieldsValue({ duration_hours: viewStandbyPeriod.duration_hours });
            return;
        }

        const nowInSiteTimezone = toZonedTime(new Date(), standbyPeriod.site!.site_timezone);

        const diffInMinutes = differenceInMinutes(nowInSiteTimezone, datetimeStart);

        const durationHours = diffInMinutes > 0 ? Math.round((diffInMinutes / 60) * 100) / 100 : 0;

        form.setFieldsValue({ duration_hours: durationHours });
    }

    function resourcesValidator({ getFieldValue }: { getFieldValue: Function }) {
        return {
            validator(_: any, value: any) {
                return resources?.length
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please select at least one site from the left section of the calendar!'));
            },
        };
    }

    //create standby period
    const createStandbyPeriodWithConfirmation = () => setFormAction(FORM_ACTION.CREATE);

    const createStandbyPeriodInBatchAction = async () => {
        setLoading(true);
        try {
            const preparedStandbyPeriod = await form.validateFields();
            const { standbyPeriod, sites } = toBatchRequest(preparedStandbyPeriod, resources);
            await createStandbyPeriodBatch(standbyPeriod, sites);
            notification.info({ key: 'standby-period-batch-save-success', message: 'Standby Periods saved!' });
            onClose(preparedStandbyPeriod);
        } catch (err: any) {
            notification.error({ key: 'standby-period-batch-save-error', message: err.message || 'Cannot save Standby Periods!' });
        }
        setLoading(false);
    };

    //update standby period
    const updateStandbyPeriodWithConfirmation = () => {
        setFormAction(FORM_ACTION.UPDATE);
        setAffectedSites(resources);
    };
    const updateStandbyPeriodAction = async () => {
        const id = standbyPeriod.id!;

        if (!id) return;
        setLoading(true);
        try {
            const preparedStandbyPeriod = await form.validateFields();
            const { standbyPeriod } = toBatchRequest(preparedStandbyPeriod);
            await updateStandbyPeriod(id, standbyPeriod);
            notification.info({ key: 'standby-period-update-success', message: 'Standby Period updated!' });
            onClose(preparedStandbyPeriod);
        } catch (err: any) {
            notification.error({ key: 'standby-period-update-error', message: err.message || 'Cannot update Standby Period!' });
        }
        setLoading(false);
    };

    // update batch standby period
    const updateStandbyPeriodInBatchWithConfirmation = () => {
        form.validateFields().then(() => {
            setFormAction(FORM_ACTION.UPDATE_BATCH);
            setAffectedSites(batchSites);
        });
    };
    const updateStandbyPeriodInBatchForCompanyWithConfirmation = () => {
        form.validateFields().then(() => {
            setFormAction(FORM_ACTION.UPDATE_BATCH_IN_COMPANY);
            const affectedSitesInCompany = batchSites.filter(site => site.company_id === standbyPeriod.site?.company_id);
            setAffectedSites(affectedSitesInCompany);
        });
    };
    const updateStandbyPeriodInBatchAction = async (companyId?: number) => {
        const batchUuid = standbyPeriod.batch_uuid!;

        if (!batchUuid) return;
        setLoading(true);
        try {
            const preparedStandbyPeriod = await form.validateFields();
            const { standbyPeriod } = toBatchRequest(preparedStandbyPeriod);
            await updateStandbyPeriodBatch(batchUuid, standbyPeriod, companyId);
            notification.info({ key: 'standby-period-batch-update-success', message: 'Batch was updated!' });
            onClose(preparedStandbyPeriod);
        } catch (error: any) {
            notification.error({ key: 'standby-period-batch-update-error', message: error.message || 'Cannot update batch!' });
        }
    };

    // cancel one standby period
    const cancelStandbyPeriodWithConfirmation = () => {
        setAffectedSites(resources);
        setFormAction(FORM_ACTION.CANCEL);
    };
    const cancelStandbyPeriodAction = async () => {
        const id = standbyPeriod.id!;

        if (!id) return;
        setLoading(true);

        try {
            await cancelStandbyPeriod(id);
            notification.info({ key: 'standby-period-cancel-success', message: 'Standby Period canceled!' });
            onClose(standbyPeriod);
        } catch (error: any) {
            notification.error({ key: 'standby-period-cancel-error', message: error.message || 'Cannot cancel Standby Period!' });
        }
    };

    //cancel batch standby period
    const cancelStandbyPeriodInBatchWithConfirmation = () => {
        setFormAction(FORM_ACTION.CANCEL_BATCH);
        setAffectedSites(batchSites);
    };
    const cancelStandbyPeriodInBatchForCompanyWithConfirmation = () => {
        setFormAction(FORM_ACTION.CANCEL_BATCH_IN_COMPANY);
        const affectedSitesInCompany = batchSites.filter(site => site.company_id === standbyPeriod.site?.company_id);
        setAffectedSites(affectedSitesInCompany);
    };
    const cancelStandbyPeriodInBatchAction = async (companyId?: number) => {
        const batchUuid = standbyPeriod.batch_uuid!;

        if (!batchUuid) return;
        setLoading(true);
        try {
            await cancelStandbyPeriodBatch(batchUuid, companyId);
            notification.info({ key: 'standby-period-batch-cancel-success', message: 'Batch was cancelled!' });
            onClose(standbyPeriod);
        } catch (error: any) {
            notification.error({ key: 'standby-period-batch-cancel-error', message: error.message || 'Cannot cancel batch!' });
        }
    };

    const eventConfirmationModalAction = {
        [FORM_ACTION.CANCEL]: () => {
            cancelStandbyPeriodAction();
            closeStandbyPeriodConfirmationModal();
        },
        [FORM_ACTION.CANCEL_BATCH]: () => {
            cancelStandbyPeriodInBatchAction(customerCompanyId);
            closeStandbyPeriodConfirmationModal();
        },
        [FORM_ACTION.CANCEL_BATCH_IN_COMPANY]: () => {
            const companyId = auth?.user?.isAdmin() ? standbyPeriod.site?.company_id : customerCompanyId;
            cancelStandbyPeriodInBatchAction(companyId);
            closeStandbyPeriodConfirmationModal();
        },
        [FORM_ACTION.UPDATE]: () => {
            updateStandbyPeriodAction();
            closeStandbyPeriodConfirmationModal();
        },
        [FORM_ACTION.UPDATE_BATCH]: () => {
            updateStandbyPeriodInBatchAction(customerCompanyId);
            closeStandbyPeriodConfirmationModal();
        },
        [FORM_ACTION.UPDATE_BATCH_IN_COMPANY]: () => {
            const companyId = auth?.user?.isAdmin() ? standbyPeriod.site?.company_id : customerCompanyId;
            updateStandbyPeriodInBatchAction(companyId);
            closeStandbyPeriodConfirmationModal();
        },
        [FORM_ACTION.CREATE]: () => {
            createStandbyPeriodInBatchAction();
            closeStandbyPeriodConfirmationModal();
        },
    };

    const closeStandbyPeriodConfirmationModal = () => setFormAction(null);

    const closeDialog = () => onClose();

    const cancelActionsMenu = () => {
        const companyName = toAllowedStringLength(standbyPeriod.site?.company?.company_name!, 20);
        const companySitesCount = batchSites.filter(site => site.company_id === standbyPeriod.site?.company_id).length;

        return (
            <Menu>
                <Menu.Item key="standby-period-batch-cancel-info" onClick={cancelStandbyPeriodInBatchWithConfirmation}>
                    Cancel batch ({batchSites.length})
                </Menu.Item>
                {auth?.user?.isAdmin() && companySitesCount > 1 && (
                    <Menu.Item key="standby-period-batch-cancel-for-company-info" onClick={cancelStandbyPeriodInBatchForCompanyWithConfirmation}>
                        Cancel in <strong>{companyName}</strong>({companySitesCount})
                    </Menu.Item>
                )}
            </Menu>
        );
    };

    const updateActionsMenu = () => {
        const companyName = toAllowedStringLength(standbyPeriod.site?.company?.company_name!, 20);
        const companySitesCount = batchSites.filter(site => site.company_id === standbyPeriod.site?.company_id).length;

        return (
            <Menu>
                <Menu.Item key="standby-period-batch-update-info" onClick={updateStandbyPeriodInBatchWithConfirmation}>
                    Update batch ({batchSites.length})
                </Menu.Item>
                {auth?.user?.isAdmin() && companySitesCount > 1 && (
                    <Menu.Item key="standby-period-batch-update-for-company-info" onClick={updateStandbyPeriodInBatchForCompanyWithConfirmation}>
                        Update in <strong>{companyName}</strong>({companySitesCount})
                    </Menu.Item>
                )}
            </Menu>
        );
    };

    return (
        <Modal
            open
            destroyOnHidden
            title={isEditMode ? 'Update Standby Period' : 'New Standby Period'}
            onCancel={closeDialog}
            footer={[
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                    <Button key="standby-period-form-close" data-cy="close-dialog" onClick={closeDialog}>
                        Close
                    </Button>

                    {!warning && (
                        <>
                            {isEditMode ? (
                                <>
                                    {batchSites.length > 1 ? (
                                        <>
                                            {!isStarted(standbyPeriod as IStandbyPeriod) && (
                                                <Dropdown.Button
                                                    onClick={cancelStandbyPeriodWithConfirmation}
                                                    overlay={cancelActionsMenu}
                                                    loading={loading}
                                                    key="cancel-standby-period-actions"
                                                >
                                                    Cancel Standby Period
                                                </Dropdown.Button>
                                            )}
                                            <Dropdown.Button
                                                onClick={updateStandbyPeriodWithConfirmation}
                                                overlay={updateActionsMenu}
                                                loading={loading}
                                                key="update-standby-period-actions"
                                            >
                                                Update
                                            </Dropdown.Button>
                                        </>
                                    ) : (
                                        <>
                                            {!isStarted(standbyPeriod as IStandbyPeriod) && (
                                                <Button
                                                    onClick={cancelStandbyPeriodWithConfirmation}
                                                    loading={loading}
                                                    key="cancel-standby-period-action"
                                                >
                                                    Cancel Standby Period
                                                </Button>
                                            )}
                                            <Button
                                                onClick={updateStandbyPeriodWithConfirmation}
                                                loading={loading}
                                                key="update-standby-period-action"
                                            >
                                                Update
                                            </Button>
                                        </>
                                    )}
                                </>
                            ) : (
                                <Button
                                    key="create-standby-period-action"
                                    type="primary"
                                    loading={loading}
                                    onClick={createStandbyPeriodWithConfirmation}
                                    disabled={!formValid}
                                >
                                    Save ({resources.length})
                                </Button>
                            )}
                        </>
                    )}
                </Space>,
            ]}
            className="standby-period-modal"
            data-cy="standby-period-by-site"
        >
            <div className="standby-period-form-container content-background" data-cy="warning-msg">
                {warning && (
                    <>
                        {warning}
                        <br />
                    </>
                )}

                <Form form={form} name="standby-period-form" preserve={false} layout="vertical" initialValues={viewStandbyPeriod}>
                    <Row>
                        <Col xs={24}>
                            <Form.Item name="resources" label={<Typography.Text strong>Resources</Typography.Text>} rules={[resourcesValidator]}>
                                <ResourceCollapsedList
                                    resources={batchSites}
                                    eventDatetime={datetimeStart}
                                    loading={isLoadingBatchSites}
                                    showTimezone
                                />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Form.Item
                                        name="datetime_start_local"
                                        required
                                        rules={[{ required: true, message: 'Please select date!' }]}
                                        label={
                                            <>
                                                <Typography.Text strong>Start time&nbsp;</Typography.Text>
                                                <Tooltip title="Standby Period will be dispatched to start at the site's local timezone">
                                                    <InfoCircleOutlined style={{ color: themeColors.colorPrimary }} />
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <DatePicker data-cy="date-picker" format="DD/MMM/YYYY HH:mm" showTime={{ format: 'HH:mm' }} size="large" />
                                    </Form.Item>
                                    <Form.Item dependencies={['datetime_start_local']} style={{ marginBottom: 0 }}>
                                        {({ getFieldValue }) => {
                                            const selectedDate = getFieldValue('datetime_start_local');

                                            return (
                                                selectedDate &&
                                                isPastDate(getFieldValue('datetime_start_local')) && (
                                                    <Typography.Text type="secondary">
                                                        <WarningOutlined style={{ color: '#faad14' }} /> Standby Period started in the past.
                                                    </Typography.Text>
                                                )
                                            );
                                        }}
                                    </Form.Item>
                                </Space>
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Form.Item
                                        label={
                                            <>
                                                <Typography.Text strong>Duration</Typography.Text>{' '}
                                                {isEditMode && isStarted(standbyPeriod as IStandbyPeriod) && (
                                                    <Button key="end-now-button" size="small" type="link" onClick={setDurationToNow}>
                                                        End Now
                                                    </Button>
                                                )}
                                            </>
                                        }
                                    >
                                        <Space>
                                            <Form.Item name="duration_hours" noStyle>
                                                <InputNumber data-cy="duration-hours" min={0} max={48} step={0.25} size="large" />
                                            </Form.Item>
                                            <Typography.Text>hours</Typography.Text>
                                        </Space>
                                    </Form.Item>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24}>
                            <Form.Item
                                required={!isEditMode}
                                label={<Typography.Text strong>Associate with Program</Typography.Text>}
                                name="program_id"
                                rules={[{ required: !isEditMode, message: 'Please select program!' }]}
                            >
                                <ProgramSelector
                                    handleProgram={() => null}
                                    selectedProgram={programId}
                                    excludeUtilityCustomerPrograms={
                                        auth?.user?.user_type === UserType.CUSTOMER || auth?.user?.user_type === UserType.OPERATOR
                                    }
                                    disabled={isEditMode}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <CalendarEventPreview
                        isStandbyPeriod={true}
                        preEventDurationHours={0}
                        eventDurationHours={durationHours}
                        postEventDurationHours={0}
                        secondaryPreEventDurationHours={0}
                    />
                </Form>

                {formAction && (
                    <EventConfirmationModal
                        action={formAction}
                        handleConfirm={eventConfirmationModalAction[formAction]}
                        handleClose={closeStandbyPeriodConfirmationModal}
                        modalContent={
                            <StandbyPeriodConfirmationModalContent
                                standbyPeriod={{
                                    ...form.getFieldsValue(),
                                }}
                                affectedSites={affectedSites}
                                actionType={CONFIRMATION_TITLE[formAction] as StandbyPeriodActionType}
                            />
                        }
                    />
                )}
            </div>
        </Modal>
    );
};
